/**
 * Hiring-demand board — putting a role in the right team.
 *
 * WHY THIS EXISTS. Nothing upstream knows which team a role belongs to: the
 * Hiring Database has Department, Unit and job family, and stops there. The
 * third level is therefore DERIVED, from the job-family map in
 * `proxy-server/lib/taDemandTeams.js` — which is a guess about a family, and
 * deliberately an incomplete one. This is where the lead who knows better
 * corrects it for one seat, and the correction outranks the map.
 *
 * TWO WAYS IN, and both are needed. Dragging is the one Theresa asked for and
 * the quick one, but a drag can only reach a team that is on screen and cannot
 * be done from a keyboard — so every role row also carries a picker listing
 * every team the unit has, including the empty ones and "no team yet".
 *
 * A ROLE NEVER LEAVES ITS UNIT this way. Which unit a role sits in is the HDB's
 * own `Unit` column and a real org fact; the team is ours to arrange. Dropping a
 * Sales role onto a Marketing team would silently disagree with Airtable, so the
 * drop zones refuse anything from another unit.
 */
import { useCallback, useState } from 'react';
import { FolderInput, X } from 'lucide-react';
import type { BoardRole } from '../../../types/taDemand';
import { selectCls } from './demandUi';

/** What a role row and a drop target need from the shared drag state. */
export interface TeamMoveApi {
  /** The role currently being dragged, if any. */
  draggingId: string | null;
  dragProps: (role: BoardRole, unitKey: string) => React.HTMLAttributes<HTMLElement>;
  /** Handlers + highlight state for one team header (or the loose group). */
  dropProps: (unitKey: string, team: string) => {
    active: boolean;
    handlers: React.HTMLAttributes<HTMLElement>;
  };
  move: (role: BoardRole, team: string) => void;
}

/**
 * The drag is tracked in STATE rather than read off `dataTransfer`.
 *
 * `dataTransfer.getData` is empty during `dragover` in every browser — it only
 * yields on `drop` — so a target that needed the payload to decide whether to
 * highlight could never highlight. The state also carries the source unit,
 * which is what lets a target refuse a role from somewhere else instead of
 * accepting it and quietly disagreeing with Airtable.
 */
export function useTeamMove(onMove: (role: BoardRole, team: string) => void): TeamMoveApi {
  const [drag, setDrag] = useState<{ role: BoardRole; unitKey: string } | null>(null);
  const [hover, setHover] = useState<string | null>(null);

  const dragProps = useCallback(
    (role: BoardRole, unitKey: string) => ({
      draggable: true,
      onDragStart: (e: React.DragEvent) => {
        setDrag({ role, unitKey });
        // Firefox starts no drag at all unless something is set.
        e.dataTransfer.setData('text/plain', role.id);
        e.dataTransfer.effectAllowed = 'move';
      },
      onDragEnd: () => {
        setDrag(null);
        setHover(null);
      },
    }),
    [],
  );

  const dropProps = useCallback(
    (unitKey: string, team: string) => {
      const key = `${unitKey}|${team}`;
      const takes = Boolean(drag) && drag?.unitKey === unitKey && (drag?.role.team ?? '') !== team;
      return {
        active: takes && hover === key,
        handlers: {
          onDragOver: (e: React.DragEvent) => {
            if (!takes) return;
            // Without preventDefault the browser refuses the drop outright.
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setHover(key);
          },
          onDragLeave: () => setHover((h) => (h === key ? null : h)),
          onDrop: (e: React.DragEvent) => {
            if (!takes || !drag) return;
            e.preventDefault();
            e.stopPropagation();
            onMove(drag.role, team);
            setDrag(null);
            setHover(null);
          },
        },
      };
    },
    [drag, hover, onMove],
  );

  return { draggingId: drag?.role.id ?? null, dragProps, dropProps, move: onMove };
}

/**
 * The complete way to move a role, for the teams a drag cannot reach.
 *
 * Closed it is one ghost icon, revealed on hover like the delete button beside
 * it, so a board of 600 rows does not carry 600 dropdowns. Open it is a native
 * `<select>` — same reasoning already recorded for the cockpit's other dense
 * controls: the design-system trigger renders at 16px inside a 13px grid and
 * wraps a team name onto two lines, and a native select brings its own
 * positioning and keyboard handling for free.
 */
export function MoveTeamControl({
  role,
  teams,
  busy,
  onMove,
}: {
  role: BoardRole;
  /** Every team the role's unit has, chart order first. */
  teams: string[];
  busy: boolean;
  onMove: (team: string) => void;
}) {
  const [open, setOpen] = useState(false);
  /**
   * The chosen team is STAGED and committed when the control closes.
   *
   * A native select fires `change` on every arrow key on Windows and Linux, so
   * committing on change would persist each team the keyboard passes over —
   * a server round-trip and a board reload per keystroke, ending on whichever
   * option the caret happened to reach first. Staging costs a mouse user
   * nothing (picking an option closes the select, which commits) and gives a
   * keyboard user the whole list.
   */
  const [staged, setStaged] = useState<string | null>(null);

  function close(commit: boolean) {
    if (commit && staged !== null && staged !== (role.team ?? '')) onMove(staged);
    setStaged(null);
    setOpen(false);
  }

  if (!teams.length) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={busy}
        title={role.team ? `Move out of ${role.team}` : 'Put this role in a team'}
        aria-label={`Change the team for ${role.title}`}
        className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm text-gray-400 opacity-0 hover:bg-gray-100 hover:text-blue-700 focus:opacity-100 disabled:opacity-40 group-hover:opacity-100"
      >
        <FolderInput className="h-3 w-3" />
      </button>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-1">
      <select
        autoFocus
        className={selectCls}
        value={staged ?? role.team ?? ''}
        aria-label={`Team for ${role.title}`}
        onChange={(e) => setStaged(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            close(true);
          }
          if (e.key === 'Escape') close(false);
        }}
        onBlur={() => close(true)}
      >
        {/* Blank is a real choice: it CLEARS a team the map assigned, which is
            the only way to undo a wrong map entry. */}
        <option value="">No team yet</option>
        {teams.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      {/* Cancel must run BEFORE the select's blur commits, so it fires on
          mousedown rather than click. */}
      <button
        type="button"
        onMouseDown={(e) => {
          e.preventDefault();
          close(false);
        }}
        aria-label="Cancel"
        className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-sm text-gray-400 hover:bg-gray-100"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

/** The tint a group header takes while a role is hovering over it. */
export const dropTint = (active: boolean) =>
  active ? 'outline outline-2 -outline-offset-2 outline-blue-500 bg-blue-100' : '';
